import { Join } from 'design-studio/lib'

interface DiagramReasoningCorrectnessProps {
  className?: string
  blueClassName?: string
  axisTitleClassName?: string
}

export function DiagramReasoningCorrectness({
  className,
  blueClassName = 'text-blue-500 dark:text-blue-300',
  axisTitleClassName = 'text-slate-500 dark:text-slate-300',
}: DiagramReasoningCorrectnessProps) {
  return (
    <svg
      viewBox="0 0 560 315"
      className={Join('h-auto w-full', className)}
      fill="none"
      aria-label="Chart: Reasoning correctness vs Software complexity"
    >
      <defs>
        {/* Stroke gradient: orange → red (LLM-only declining curve) */}
        <linearGradient
          id="rcLineGrad"
          x1="78"
          y1="44"
          x2="524"
          y2="252"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#dc2626" />
        </linearGradient>
        {/* Area between the flat SpecLogician line and the declining LLM curve */}
        <linearGradient
          id="rcGapFill"
          x1="0"
          y1="44"
          x2="0"
          y2="262"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#f97316" stopOpacity="0.02" />
          <stop offset="100%" stopColor="#dc2626" stopOpacity="0.13" />
        </linearGradient>
        {/* Area under the flat SpecLogician line (whole maintained-correctness zone) */}
        <linearGradient
          id="rcBlueFill"
          x1="0"
          y1="44"
          x2="0"
          y2="262"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
        </linearGradient>
        <marker
          id="rcArrow"
          markerWidth="6"
          markerHeight="5"
          refX="5"
          refY="2.5"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <polygon points="0 0, 6 2.5, 0 5" fill="#94a3b8" />
        </marker>
      </defs>

      {/* Dashed horizontal grid */}
      {[106, 145, 184].map((y) => (
        <line
          key={y}
          x1="78"
          y1={y}
          x2="526"
          y2={y}
          stroke="#94a3b8"
          strokeWidth="0.6"
          strokeOpacity="0.25"
          strokeDasharray="4 5"
        />
      ))}

      {/* Axes */}
      <line
        x1="78"
        y1="262"
        x2="78"
        y2="18"
        stroke="#94a3b8"
        strokeWidth="1.5"
        markerEnd="url(#rcArrow)"
      />
      <line
        x1="78"
        y1="262"
        x2="548"
        y2="262"
        stroke="#94a3b8"
        strokeWidth="1.5"
        markerEnd="url(#rcArrow)"
      />

      {/* Y axis label */}
      <g transform="translate(22,145) rotate(-90)">
        <text
          textAnchor="middle"
          className={axisTitleClassName}
          fill="currentColor"
          fontSize="13"
          fontFamily="system-ui,sans-serif"
        >
          Reasoning correctness
        </text>
      </g>

      {/* X axis label */}
      <text
        x="304"
        y="305"
        textAnchor="middle"
        className={axisTitleClassName}
        fill="currentColor"
        fontSize="13"
        fontFamily="system-ui,sans-serif"
      >
        Software complexity
      </text>

      {/* Blue area: full zone under the SpecLogician flat line (maintained correctness) */}
      <path d="M 78 44 L 524 44 L 524 262 L 78 262 Z" fill="url(#rcBlueFill)" />

      {/* Orange "lost correctness" gap — between flat line and declining LLM curve */}
      <path
        d="M 78 44 L 524 44 L 524 252 C 478 238 428 216 370 180 C 282 124 172 52 78 44 Z"
        fill="url(#rcGapFill)"
      />

      {/* LLM-only declining curve */}
      <path
        d="M 78 44 C 172 52 282 124 370 180 C 428 216 478 238 524 252"
        stroke="url(#rcLineGrad)"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* LLM + SpecLogician flat line */}
      <path
        d="M 78 44 L 524 44"
        className={blueClassName}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Curve labels */}
      <text
        x="300"
        y="34"
        textAnchor="middle"
        className={blueClassName}
        fill="currentColor"
        fontSize="11"
        fontFamily="system-ui,sans-serif"
      >
        LLM + SpecLogician
      </text>
      <text
        x="295"
        y="165"
        fill="#ea580c"
        fontSize="11"
        fontFamily="system-ui,sans-serif"
      >
        LLM-only
      </text>
    </svg>
  )
}
